export const DOCUMENT_UPLOAD_MAX_SIZE_BYTES = 25 * 1024 * 1024;
export const DOCUMENT_UPLOAD_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;

export type DocumentUploadContentType = (typeof DOCUMENT_UPLOAD_CONTENT_TYPES)[number];

export type CreateDocumentUploadInput = {
  filename: string;
  content_type: DocumentUploadContentType;
  size_bytes: number;
  folder_id?: string | null;
};

export type DocumentUploadPreparation = {
  document_id: string;
  status: "pending_upload";
  upload_url: string;
  upload: {
    method: "PUT";
    headers: Record<string, string>;
    expires_in_seconds: number;
  };
};

export type CompletedDocumentUpload = {
  document_id: string;
  status: "available";
  uploaded_at?: string;
};

export type DocumentFile = {
  document_id: string;
  title: string | null;
  original_filename: string;
  content_type: DocumentUploadContentType;
  size_bytes: number;
  status: "available" | "trashed";
  folder_id?: string;
  uploaded_at?: string | null;
  trashed_at?: string | null;
};

export type DocumentFolder = {
  folder_id: string;
  name: string;
  status: "active" | "deleted";
  parent_folder_id?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
};

export type DocumentUploadProgress = {
  lengthComputable: boolean;
  loaded: number;
  total: number | null;
  percentage: number | null;
};

type DocumentUploadProgressHandler = (progress: DocumentUploadProgress) => void;

type CreateUploadResponse = Partial<DocumentUploadPreparation>;
type CompleteUploadResponse = Partial<CompletedDocumentUpload>;
type DocumentFileResponse = Partial<DocumentFile>;
type ListDocumentsResponse = {
  documents?: DocumentFileResponse[];
};
type DocumentFolderResponse = Partial<DocumentFolder>;
type ListFoldersResponse = {
  folders?: DocumentFolderResponse[];
};
type CreateDownloadUrlResponse = {
  document_id?: string;
  download_url?: string;
  expires_in_seconds?: number;
  variant?: string;
};
type DeleteDocumentResponse = {
  document_id?: string;
  status?: string;
};
export type EmptyTrashResult = {
  purging_count: number;
};
type EmptyTrashResponse = Partial<EmptyTrashResult>;

export class DocumentServiceError extends Error {
  constructor(message = "We could not upload your document right now.") {
    super(message);
    this.name = "DocumentServiceError";
  }
}

function isSupportedDocumentContentType(value: string): value is DocumentUploadContentType {
  return DOCUMENT_UPLOAD_CONTENT_TYPES.includes(value as DocumentUploadContentType);
}

export function validateDocumentFile(file: File | null): string | null {
  if (!file) {
    return "Choose a PDF, JPEG or PNG document to upload.";
  }

  if (!isSupportedDocumentContentType(file.type)) {
    return "Choose a PDF, JPEG or PNG document.";
  }

  if (file.size <= 0) {
    return "Choose a document that is not empty.";
  }

  if (file.size > DOCUMENT_UPLOAD_MAX_SIZE_BYTES) {
    return "Choose a document smaller than 25 MiB.";
  }

  return null;
}

async function requestDocumentService<TBody>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "DELETE" | "GET" | "PATCH" | "POST",
  body?: TBody,
  fallbackMessage = "We could not upload your document right now.",
) {
  if (!accessToken) {
    throw new DocumentServiceError("Please sign in again to upload health documents.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 409) {
      const body = (await response.json().catch(() => null)) as { error?: { message?: unknown } } | null;
      const message = body?.error?.message;
      throw new DocumentServiceError(typeof message === "string" && message.trim() ? message : fallbackMessage);
    }

    if (response.status === 400) {
      throw new DocumentServiceError("Please check the document details and try again.");
    }

    if (response.status === 401 || response.status === 403) {
      throw new DocumentServiceError("Please sign in again to manage your files.");
    }

    throw new DocumentServiceError(fallbackMessage);
  }

  return (await response.json()) as unknown;
}

function normalizeCreateUploadResponse(data: CreateUploadResponse): DocumentUploadPreparation {
  if (
    typeof data.document_id !== "string" ||
    data.status !== "pending_upload" ||
    typeof data.upload_url !== "string" ||
    data.upload?.method !== "PUT" ||
    !data.upload.headers ||
    typeof data.upload.expires_in_seconds !== "number"
  ) {
    throw new DocumentServiceError("MedoPilot could not prepare the document upload.");
  }

  return {
    document_id: data.document_id,
    status: data.status,
    upload_url: data.upload_url,
    upload: {
      method: data.upload.method,
      headers: data.upload.headers,
      expires_in_seconds: data.upload.expires_in_seconds,
    },
  };
}

function normalizeCompleteUploadResponse(data: CompleteUploadResponse): CompletedDocumentUpload {
  if (typeof data.document_id !== "string" || data.status !== "available") {
    throw new DocumentServiceError("MedoPilot could not finalize the document upload.");
  }

  return {
    document_id: data.document_id,
    status: data.status,
    uploaded_at: typeof data.uploaded_at === "string" ? data.uploaded_at : undefined,
  };
}

function normalizeDocumentFile(data: DocumentFileResponse, expectedStatus: "available" | "trashed" = "available"): DocumentFile {
  const contentType = data.content_type;
  if (
    typeof data.document_id !== "string" ||
    typeof data.original_filename !== "string" ||
    typeof contentType !== "string" ||
    !isSupportedDocumentContentType(contentType) ||
    typeof data.size_bytes !== "number" ||
    data.status !== expectedStatus
  ) {
    throw new DocumentServiceError("MedoPilot could not load your files.");
  }

  return {
    document_id: data.document_id,
    title: typeof data.title === "string" && data.title.trim() ? data.title : null,
    original_filename: data.original_filename,
    content_type: contentType,
    size_bytes: data.size_bytes,
    status: data.status,
    folder_id: typeof data.folder_id === "string" && data.folder_id.trim() ? data.folder_id : undefined,
    uploaded_at: typeof data.uploaded_at === "string" ? data.uploaded_at : null,
    trashed_at: typeof data.trashed_at === "string" ? data.trashed_at : null,
  };
}

function normalizeListDocumentsResponse(data: ListDocumentsResponse, expectedStatus: "available" | "trashed" = "available"): DocumentFile[] {
  if (!Array.isArray(data.documents)) {
    throw new DocumentServiceError("MedoPilot could not load your files.");
  }

  return data.documents.map((document) => normalizeDocumentFile(document, expectedStatus));
}

function normalizeDocumentFolder(data: DocumentFolderResponse): DocumentFolder {
  if (
    typeof data.folder_id !== "string" ||
    typeof data.name !== "string" ||
    (data.status !== "active" && data.status !== "deleted") ||
    typeof data.created_at !== "string" ||
    typeof data.updated_at !== "string"
  ) {
    throw new DocumentServiceError("MedoPilot could not load your folders.");
  }

  return {
    folder_id: data.folder_id,
    name: data.name,
    status: data.status,
    parent_folder_id: typeof data.parent_folder_id === "string" && data.parent_folder_id.trim() ? data.parent_folder_id : undefined,
    created_at: data.created_at,
    updated_at: data.updated_at,
    deleted_at: typeof data.deleted_at === "string" ? data.deleted_at : undefined,
  };
}

function normalizeListFoldersResponse(data: ListFoldersResponse): DocumentFolder[] {
  if (!Array.isArray(data.folders)) {
    throw new DocumentServiceError("MedoPilot could not load your folders.");
  }

  return data.folders.map(normalizeDocumentFolder);
}

function normalizeDownloadUrlResponse(data: CreateDownloadUrlResponse, documentId: string) {
  if (data.document_id !== documentId || typeof data.download_url !== "string" || typeof data.expires_in_seconds !== "number") {
    throw new DocumentServiceError("MedoPilot could not prepare your file download.");
  }

  return {
    document_id: data.document_id,
    download_url: data.download_url,
    expires_in_seconds: data.expires_in_seconds,
    variant: typeof data.variant === "string" ? data.variant : "original",
  };
}

function normalizeDeleteDocumentResponse(data: DeleteDocumentResponse, documentId: string) {
  if (data.document_id !== documentId || data.status !== "trashed") {
    throw new DocumentServiceError("MedoPilot could not remove this file.");
  }

  return {
    document_id: data.document_id,
    status: data.status,
  };
}

function normalizeEmptyTrashResponse(data: EmptyTrashResponse): EmptyTrashResult {
  if (typeof data.purging_count !== "number" || !Number.isFinite(data.purging_count) || data.purging_count < 0) {
    throw new DocumentServiceError("MedoPilot could not empty Trash.");
  }

  return { purging_count: data.purging_count };
}

export function createDocumentServiceClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async listDocuments(folderId?: string | null) {
      const query = folderId ? `?${new URLSearchParams({ folder_id: folderId }).toString()}` : "";
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents${query}`,
        "GET",
        undefined,
        "MedoPilot could not load your files.",
      )) as ListDocumentsResponse;
      return normalizeListDocumentsResponse(data);
    },
    async listFolders() {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/folders",
        "GET",
        undefined,
        "MedoPilot could not load your folders.",
      )) as ListFoldersResponse;
      return normalizeListFoldersResponse(data);
    },
    async createFolder(name: string, parentFolderId?: string | null) {
      const parentFolderIdValue = typeof parentFolderId === "string" && parentFolderId.trim() && parentFolderId !== "root" ? parentFolderId : null;
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/folders",
        "POST",
        { name, parent_folder_id: parentFolderIdValue },
        "MedoPilot could not create this folder.",
      )) as DocumentFolderResponse;
      return normalizeDocumentFolder(data);
    },
    async renameFolder(folderId: string, name: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/folders/${encodeURIComponent(folderId)}`,
        "PATCH",
        { name },
        "MedoPilot could not rename this folder.",
      )) as DocumentFolderResponse;
      return normalizeDocumentFolder(data);
    },
    async moveFolder(folderId: string, parentFolderId: string | null) {
      const parentFolderIdValue = typeof parentFolderId === "string" && parentFolderId.trim() && parentFolderId !== "root" ? parentFolderId : null;
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/folders/${encodeURIComponent(folderId)}`,
        "PATCH",
        { parent_folder_id: parentFolderIdValue },
        "MedoPilot could not move this folder.",
      )) as DocumentFolderResponse;
      return normalizeDocumentFolder(data);
    },
    async deleteFolder(folderId: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/folders/${encodeURIComponent(folderId)}`,
        "DELETE",
        undefined,
        "MedoPilot could not delete this folder.",
      )) as DocumentFolderResponse;
      return normalizeDocumentFolder(data);
    },
    async listTrashedDocuments() {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/documents/trash",
        "GET",
        undefined,
        "MedoPilot could not load Trash.",
      )) as ListDocumentsResponse;
      return normalizeListDocumentsResponse(data, "trashed");
    },
    async createUpload(payload: CreateDocumentUploadInput) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/documents/uploads",
        "POST",
        payload,
      )) as CreateUploadResponse;
      return normalizeCreateUploadResponse(data);
    },
    async uploadFileToPresignedUrl(upload: DocumentUploadPreparation, file: File, onProgress?: DocumentUploadProgressHandler) {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open(upload.upload.method, upload.upload_url, true);
        Object.entries(upload.upload.headers).forEach(([name, value]) => {
          xhr.setRequestHeader(name, value);
        });

        xhr.upload.onprogress = (event) => {
          onProgress?.({
            lengthComputable: event.lengthComputable,
            loaded: event.loaded,
            total: event.lengthComputable ? event.total : null,
            percentage: event.lengthComputable && event.total > 0 ? Math.round((event.loaded / event.total) * 100) : null,
          });
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
            return;
          }

          reject(new DocumentServiceError("The document file could not be uploaded."));
        };

        xhr.onerror = () => reject(new DocumentServiceError("The document file could not be uploaded."));
        xhr.onabort = () => reject(new DocumentServiceError("The document upload was canceled."));
        xhr.send(file);
      });
    },
    async completeUpload(documentId: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}/complete-upload`,
        "POST",
      )) as CompleteUploadResponse;
      return normalizeCompleteUploadResponse(data);
    },
    async createDownloadUrl(
      documentId: string,
      disposition: "attachment" | "inline" = "inline",
      variant: "original" | "preview" | "thumbnail" = "original",
    ) {
      const params = new URLSearchParams();
      if (disposition === "attachment") {
        params.set("disposition", "attachment");
      }
      if (variant !== "original") {
        params.set("variant", variant);
      }
      const query = params.toString() ? `?${params.toString()}` : "";
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}/download-url${query}`,
        "GET",
        undefined,
        "MedoPilot could not prepare your file download.",
      )) as CreateDownloadUrlResponse;
      return normalizeDownloadUrlResponse(data, documentId);
    },
    async renameDocument(documentId: string, filename: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}`,
        "PATCH",
        { filename },
        "MedoPilot could not rename this file.",
      )) as DocumentFileResponse;
      return normalizeDocumentFile(data);
    },
    async moveDocument(documentId: string, folderId: string | null) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}/folder`,
        "PATCH",
        { folder_id: folderId },
        "MedoPilot could not move this file.",
      )) as DocumentFileResponse;
      return normalizeDocumentFile(data);
    },
    async deleteDocument(documentId: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}`,
        "DELETE",
        undefined,
        "MedoPilot could not remove this file.",
      )) as DeleteDocumentResponse;
      return normalizeDeleteDocumentResponse(data, documentId);
    },
    async restoreDocument(documentId: string) {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        `/v1/documents/${encodeURIComponent(documentId)}/restore`,
        "POST",
        undefined,
        "MedoPilot could not restore this file.",
      )) as DocumentFileResponse;
      return normalizeDocumentFile(data);
    },
    async emptyTrash() {
      const data = (await requestDocumentService(
        apiBaseUrl,
        getAccessToken(),
        "/v1/documents/trash/empty",
        "POST",
        undefined,
        "MedoPilot could not empty Trash.",
      )) as EmptyTrashResponse;
      return normalizeEmptyTrashResponse(data);
    },
  };
}
